import type { ReactNode } from 'react';
import { cn } from './cn';

/**
 * A status pill.
 *
 * Colour is never the only signal — every badge carries its label as text.
 * Roughly one in twelve men has a colour vision deficiency, and this is a
 * workforce that skews heavily male; a red dot that means "unfit" and a green
 * one that means "fit" are the same dot to a meaningful share of users.
 *
 * The tones are split into clinical and operational deliberately. An overdue
 * examination and an unfit worker are not the same kind of bad, and a shared
 * palette would quietly claim they were.
 */
export type Tone =
  // Clinical — the four fitness outcomes, and no fewer.
  | 'fit' | 'restricted' | 'tempUnfit' | 'unfit'
  // Operational — compliance and workflow.
  | 'covered' | 'dueSoon' | 'overdue' | 'pending'
  // Generic semantic, for states that are neither of the above.
  | 'success' | 'info' | 'warning' | 'danger' | 'neutral';

const TONE: Record<Tone, string> = {
  fit: 'bg-fit-100 text-fit-600',
  restricted: 'bg-restricted-100 text-restricted-600',
  tempUnfit: 'bg-tempUnfit-100 text-tempUnfit-600',
  unfit: 'bg-unfit-100 text-unfit-600',

  covered: 'bg-covered-100 text-covered-600',
  dueSoon: 'bg-dueSoon-100 text-dueSoon-600',
  overdue: 'bg-overdue-100 text-overdue-600',
  pending: 'bg-pending-100 text-pending-600',

  success: 'bg-success-100 text-success-600',
  info: 'bg-info-100 text-info-600',
  warning: 'bg-warning-100 text-warning-600',
  danger: 'bg-danger-100 text-danger-600',
  neutral: 'bg-neutral-100 text-neutral-600',
};

export function StatusBadge({ tone = 'neutral', dot, className, children }: {
  tone?: Tone;
  /** A leading dot, for a dense table where the pill would crowd the row. */
  dot?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-badge px-2 py-0.5',
        'text-caption font-medium',
        TONE[tone], className,
      )}
    >
      {dot && <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />}
      {children}
    </span>
  );
}

/**
 * The fitness decision, drawn to dominate.
 *
 * On a certificate or a review screen this is the single most important thing
 * on the page and it is sized to say so. A worker, a gate guard and a factory
 * inspector all read this line and nothing else, and a 12px pill among twenty
 * other 12px pills is not what any of them needs.
 */
// The labels are FITNESS_LABEL's (@dru/types), word for word. This banner
// said "Medically fit" and "Not fit" where every other screen, the
// certificate and the worker's app say "Fit for duty" and "Unfit for duty" —
// so the signing dialog showed two names for one decision side by side.
const DECISION: Record<string, { tone: Tone; mark: string; label: string }> = {
  FIT: { tone: 'fit', mark: '✓', label: 'Fit for duty' },
  FIT_WITH_RESTRICTION: { tone: 'restricted', mark: '!', label: 'Fit with restrictions' },
  TEMPORARILY_UNFIT: { tone: 'tempUnfit', mark: '⏸', label: 'Temporarily unfit' },
  FURTHER_EVALUATION: { tone: 'pending', mark: '…', label: 'Further evaluation required' },
  UNFIT: { tone: 'unfit', mark: '✕', label: 'Unfit for duty' },
};

const BANNER: Record<Tone, string> = {
  fit: 'bg-fit-100 text-fit-600 border-fit-600/20',
  restricted: 'bg-restricted-100 text-restricted-600 border-restricted-600/20',
  tempUnfit: 'bg-tempUnfit-100 text-tempUnfit-600 border-tempUnfit-600/20',
  unfit: 'bg-unfit-100 text-unfit-600 border-unfit-600/20',
  covered: '', dueSoon: '', overdue: '', pending: '',
  success: '', info: '', warning: '', danger: '', neutral: '',
};

export function FitnessDecision({ decision, detail, className }: {
  decision: string;
  /** The restriction, or the retest date — the thing that makes it actionable. */
  detail?: ReactNode;
  className?: string;
}) {
  const d = DECISION[decision];
  if (!d) {
    return <StatusBadge tone="neutral" className={className}>{decision}</StatusBadge>;
  }
  return (
    <div className={cn('rounded-card border px-4 py-3', BANNER[d.tone], className)}>
      <div className="flex items-center gap-2.5">
        <span aria-hidden className="text-h2 leading-none">{d.mark}</span>
        <span className="text-h3 font-bold uppercase tracking-wide">{d.label}</span>
      </div>
      {detail && <div className="mt-1.5 text-body text-neutral-700">{detail}</div>}
    </div>
  );
}

/**
 * The mappings from domain state to tone.
 *
 * Kept here rather than at each call site so that a fitness decision is the
 * same colour in the physician's queue, the HR register and the certificate.
 * A decision that is amber on one screen and red on another is a decision the
 * reader has to look up twice.
 */
export function coverageTone(state: string): Tone {
  switch (state) {
    case 'COVERED': return 'covered';
    case 'DUE_SOON': return 'dueSoon';
    case 'OVERDUE': return 'overdue';
    case 'IN_PROGRESS': return 'pending';
    // Never examined is not a milder form of overdue — statutory cover has
    // never existed for this worker — but it needs the same urgency.
    case 'NEVER_EXAMINED': return 'overdue';
    // Examined, not cleared. The fitness tones, so the worklist and the
    // certificate say "unfit" in the same colour.
    case 'FURTHER_EVALUATION': return 'pending';
    case 'UNFIT': return 'unfit';
    case 'TEMPORARILY_UNFIT': return 'tempUnfit';
    default: return 'neutral';
  }
}

export function fitnessTone(decision: string): Tone {
  switch (decision) {
    case 'FIT': return 'fit';
    case 'FIT_WITH_RESTRICTION': return 'restricted';
    case 'TEMPORARILY_UNFIT': return 'tempUnfit';
    case 'FURTHER_EVALUATION': return 'pending';
    case 'UNFIT': return 'unfit';
    default: return 'neutral';
  }
}
